import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";

type MobilePlatform = "ios" | "android";

/** App store links per platform. Android stays null until the app ships. */
const STORE_LINKS: Record<MobilePlatform, string | null> = {
  ios: "https://apps.apple.com/us/app/newsquawk/id6759158115",
  android: null, // pending — banner stays hidden on Android until set
};

/** Hide the banner for this long after the user dismisses it. */
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/** Detect the mobile platform from the user agent, or null on desktop/unknown. */
function getMobilePlatform(): MobilePlatform | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as "Macintosh" — disambiguate via touch points.
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || isIPadOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return null;
}

/**
 * Slim top banner shown to mobile web visitors prompting them to download the
 * native app. Links to the platform-appropriate store. Dismissible, and once
 * dismissed it stays hidden for {@link COOLDOWN_MS} before reappearing once.
 */
export const AppDownloadBanner = () => {
  const [platform] = useState<MobilePlatform | null>(getMobilePlatform);
  const [dismissedAt, setDismissedAt] = useStateWithOptionalPersistence<
    number | null
  >("appBanner:dismissedAt", null);

  const storeLink = platform ? STORE_LINKS[platform] : null;
  const withinCooldown =
    dismissedAt != null && Date.now() - dismissedAt < COOLDOWN_MS;

  if (!storeLink || withinCooldown) return null;

  return (
    <div className="flex sm:hidden items-center gap-3 bg-gray-900 border-y border-gray-700 py-2 px-3">
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissedAt(Date.now())}
        className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
      <img
        src="/favicon-192.png"
        alt="Newsquawk"
        className="h-10 w-10 shrink-0 rounded-xl ring-1 ring-white/10"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-white leading-tight">
          Newsquawk
        </p>
        <p className="truncate text-xs text-gray-400 leading-tight">
          Faster, native experience
        </p>
      </div>
      <a
        href={storeLink}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded-full bg-terminal-orange px-4 py-1.5 text-xs font-bold uppercase text-gray-950 hover:opacity-90 transition-opacity"
      >
        Get
      </a>
    </div>
  );
};
